import React, { useState, useEffect, useMemo } from "react";
import CommonModal from "../../../../common/commonmodal/CommonModal";
import CustomSelect from "../../../../common/formfields/CustomSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBuilding,
    faCreditCard,
    faUserTie,
    faWheelchair,
    faShieldHalved,
    faBolt,
    faBan
} from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../../services/apiService";

const AllocateParkingModal = ({
    isOpen,
    onClose,
    onConfirm,
    selectedSlots = [],
    allocationTypeOptions = [],
    wingOptions = [],
    floorOptions = [],
    unitOptions: initialUnitOptions = [],
    siteId
}) => {
    const [allocationType, setAllocationType] = useState("");
    const [wingId, setWingId] = useState("");
    const [floorId, setFloorId] = useState("");
    const [unitId, setUnitId] = useState("");
    const [floors, setFloors] = useState(floorOptions || []);
    const [units, setUnits] = useState(initialUnitOptions || []);
    const [isLoadingUnits, setIsLoadingUnits] = useState(false);

    // Sync floorOptions when passed from parent or fetch if missing
    useEffect(() => {
        if (floorOptions && floorOptions.length > 0) {
            setFloors(floorOptions);
        } else if (isOpen) {
            apiServices({ method: 'GET', url: '/FloorMaster/GetAllFloor' })
                .then(res => {
                    if (res?.success && res?.data) {
                        const apiData = res.data.FloorMasterData || [];
                        const siteFloors = apiData.filter(f => !siteId || f.SiteId == siteId || f.siteId == siteId);
                        setFloors(siteFloors.map(f => ({
                            label: f.FloorName || f.floorName,
                            value: f.Id || f.id || f.floorId,
                            wingId: f.WingId || f.wingId,
                            original: f
                        })));
                    }
                })
                .catch(err => console.error("Error loading floors:", err));
        }
    }, [isOpen, floorOptions, siteId]);

    const selectedWingId = wingId?.value !== undefined ? wingId.value : wingId;
    const selectedFloorId = floorId?.value !== undefined ? floorId.value : floorId;

    // Fetch units using GetAllUnitMaster with siteId, wingId, floorId, and pageSize=10000
    const fetchUnits = async (wId, fId) => {
        try {
            setIsLoadingUnits(true);
            let url = `/UnitMaster/GetAllUnitMaster?pageNumber=1&pageSize=10000`;
            if (siteId) url += `&siteId=${siteId}`;
            if (wId) url += `&wingId=${wId}`;
            if (fId) url += `&floorId=${fId}`;

            const res = await apiServices({ method: 'GET', url });
            if (res?.success && res?.data) {
                const apiData = Array.isArray(res.data.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
                const mapped = apiData.map(u => ({
                    label: `${u.UnitName || u.unitName || ''} (${u.UnitNumber || u.unitNumber || u.UnitNo || u.unitNo || ''})`.trim() || `Unit #${u.UnitId || u.Id || u.id}`,
                    value: u.UnitId || u.Id || u.id || u.unitId,
                    wingId: u.WingId || u.wingId,
                    floorId: u.FloorId || u.floorId,
                    original: u
                }));
                setUnits(mapped);
            }
        } catch (error) {
            console.error("Error fetching units:", error);
        } finally {
            setIsLoadingUnits(false);
        }
    };

    // When modal opens, initialize state and fetch units
    useEffect(() => {
        if (isOpen) {
            setWingId("");
            setFloorId("");
            setUnitId("");
            if (allocationTypeOptions.length > 0) {
                setAllocationType(allocationTypeOptions[0].value);
            }
            fetchUnits("", "");
        }
    }, [isOpen]);

    // When wing or floor selection changes, fetch units matching the filter
    const handleWingChange = (val) => {
        setWingId(val);
        setFloorId("");
        setUnitId("");
        const wVal = val?.value !== undefined ? val.value : val;
        fetchUnits(wVal, "");
    };

    const handleFloorChange = (val) => {
        setFloorId(val);
        setUnitId("");
        const fVal = val?.value !== undefined ? val.value : val;
        fetchUnits(selectedWingId, fVal);
    };

    // Filter floors based on selected wing
    const filteredFloorOptions = useMemo(() => {
        if (!selectedWingId) return floors;
        const matched = floors.filter(f => (f.wingId || f.WingId || f.original?.WingId || f.original?.wingId) == selectedWingId);
        return matched.length > 0 ? matched : floors;
    }, [floors, selectedWingId]);

    // Also filter units in-memory as a safeguard
    const filteredUnitOptions = useMemo(() => {
        let list = units;
        if (selectedWingId) {
            list = list.filter(u => {
                const wId = u.wingId || u.original?.WingId || u.original?.wingId;
                return !wId || wId == selectedWingId;
            });
        }
        if (selectedFloorId) {
            list = list.filter(u => {
                const fId = u.floorId || u.original?.FloorId || u.original?.floorId;
                return fId == selectedFloorId;
            });
        }
        return list;
    }, [units, selectedWingId, selectedFloorId]);

    const handleConfirm = () => {
        onConfirm({
            allocationTypeId: allocationType?.value !== undefined ? allocationType.value : allocationType,
            allocatedUnitId: unitId?.value !== undefined ? unitId.value : (unitId || 0),
            slots: selectedSlots
        });
        onClose();
    };

    const modalFooter = (
        <div className="flex justify-end gap-3">
            <button
                onClick={onClose}
                className="px-5 py-2 text-sm font-bold text-gray-600 dark:text-textsecondary bg-gray-100 dark:bg-actionitembg rounded-lg hover:bg-gray-200 dark:hover:opacity-85 transition-all"
            >
                Cancel
            </button>
            <button
                onClick={handleConfirm}
                disabled={selectedSlots.length === 0 || !allocationType}
                className="px-5 py-2 text-sm font-bold text-white rounded-lg transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center"
                style={{ backgroundColor: "var(--color-primaryaccent)" }}
            >
                Confirm Allocation
            </button>
        </div>
    );

    return (
        <CommonModal
            isOpen={isOpen}
            onClose={onClose}
            title="Allocate Selected Parking Bays"
            width="md:w-[680px]"
            footerContent={modalFooter}
        >
            <div className="flex flex-col gap-4 pt-1">
                {/* Selected Inventory */}
                <div>
                    <label className="block text-[11px] font-bold text-gray-800 dark:text-textprimary mb-1.5">
                        Selected Inventory ({selectedSlots.length} Bays)
                    </label>
                    <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 dark:bg-dashboardbg border border-gray-100 dark:border-bordercolor rounded-lg">
                        {selectedSlots.map((slot) => (
                            <span 
                                key={slot.id} 
                                className="px-2 py-0.5 bg-blue-50 dark:bg-actionitembg text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900 rounded-[6px] text-[10px] font-semibold tracking-wide"
                            >
                                {slot.slotNumber || slot.id}
                            </span>
                        ))}
                        {selectedSlots.length === 0 && (
                            <span className="text-xs text-gray-400">No bays selected</span>
                        )}
                    </div>
                </div>

                {/* Select Allocation Type */}
                <div>
                    <label className="block text-xs font-bold text-gray-800 dark:text-textprimary mb-2">
                        Select Allocation Type
                    </label>
                    <div className="grid grid-cols-2 gap-2.5">
                        {allocationTypeOptions.map((type) => {
                            const isSelected = allocationType === type.value;
                            return (
                                <button
                                    key={type.value}
                                    onClick={() => setAllocationType(type.value)}
                                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border-2 transition-all duration-200 text-left
                                        ${isSelected
                                            ? "border-red-500 bg-red-50/10 dark:bg-red-500/10"
                                            : "border-gray-200 dark:border-bordercolor hover:border-red-300 dark:hover:border-red-500/50 bg-white dark:bg-actionitembg"
                                        }`}
                                >
                                    <FontAwesomeIcon
                                        icon={faBuilding}
                                        className={`text-sm ${isSelected ? "text-red-500" : "text-gray-400 dark:text-textsecondary"}`}
                                    />
                                    <span className={`text-[11px] font-semibold ${isSelected ? "text-red-600 dark:text-red-400" : "text-gray-700 dark:text-textprimary"}`}>
                                        {type.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Dynamic Content based on Wing, Floor and Unit */}
                <div className="bg-gray-50 dark:bg-dashboardbg p-4 rounded-xl border border-gray-100 dark:border-bordercolor">
                    <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                                <label className="block text-[11px] font-bold text-gray-600 dark:text-textprimary mb-1.5">
                                    Target Wing
                                </label>
                                <CustomSelect
                                    options={wingOptions}
                                    value={wingId}
                                    onChange={handleWingChange}
                                    placeholder="Select a wing..."
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-gray-600 dark:text-textprimary mb-1.5">
                                    Target Floor
                                </label>
                                <CustomSelect
                                    options={filteredFloorOptions}
                                    value={floorId}
                                    onChange={handleFloorChange}
                                    placeholder="Select a floor..."
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-gray-600 dark:text-textprimary mb-1.5">
                                    Target Unit
                                </label>
                                <CustomSelect
                                    options={filteredUnitOptions}
                                    value={unitId}
                                    onChange={setUnitId}
                                    placeholder={isLoadingUnits ? "Loading units..." : (filteredUnitOptions.length === 0 ? "No units found" : "Select a unit...")}
                                    isDisabled={isLoadingUnits}
                                />
                            </div>
                        </div>
                        {filteredUnitOptions.length > 0 && (
                            <div className="text-[10px] text-gray-400 dark:text-textsecondary text-right">
                                {filteredUnitOptions.length} unit{filteredUnitOptions.length === 1 ? '' : 's'} available
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </CommonModal>
    );
};

export default AllocateParkingModal;
